import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Square, Clock, Plus, User, Phone, Gamepad2, Loader2, ShoppingCart, ArrowRightLeft } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import ConfirmDialog from "./ConfirmDialog";
import AddOrderDialog from "./AddOrderDialog";
import TransferUnitDialog from "./TransferUnitDialogSimple";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ActiveRentalCard({ rental: initialRental, onStopped, onExtended }) {
  // Use local state for rental data to have full control over updates
  const [rental, setRental] = useState(initialRental);
  
  // Sync with parent prop changes (but local state takes precedence after extend/add-order)
  useEffect(() => {
    setRental(initialRental);
  }, [initialRental?.id, JSON.stringify(initialRental?.additional_items), initialRental?.total_price, initialRental?.is_paused, initialRental?.total_pause_minutes]); // Update when rental ID, F&B items, total price, pause status, or pause duration changes
  
  // Safety check
  if (!rental || !rental.id) {
    console.error('[ActiveRentalCard] Invalid rental data:', rental);
    return null;
  }

  // Memoize isNoLimit to prevent unnecessary re-renders
  const rentalMode = useMemo(() => rental.rental_mode || 'durasi', [rental.rental_mode]);
  const isNoLimit = useMemo(() => rentalMode === 'no_limit', [rentalMode]);
  
  // Initialize timer state based on mode with safety checks
  const [timeRemaining, setTimeRemaining] = useState(() => {
    try {
      if (isNoLimit) {
        // No limit: calculate elapsed time from start_time
        const startTime = new Date(rental.start_time);
        const now = new Date();
        const elapsedSeconds = Math.floor((now - startTime) / 1000);
        return Math.max(0, elapsedSeconds);
      }
      return rental.seconds_remaining || 0;
    } catch (error) {
      console.error('[ActiveRentalCard] Error initializing timer:', error);
      return 0;
    }
  });
  
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [showAddOrderDialog, setShowAddOrderDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [extendUnit, setExtendUnit] = useState('minutes'); // 'minutes' or 'hours'
  const [extendDuration, setExtendDuration] = useState(30); // default 30 minutes
  const [loading, setLoading] = useState(false);

  // DISABLED: This useEffect causes flicker because rental.seconds_remaining can be stale
  // We rely on useEffect #2 below which calculates from rental.end_time (more accurate)
  // useEffect(() => {
  //   if (!isNoLimit && rental.seconds_remaining !== undefined && !justExtendedRef.current) {
  //     setTimeRemaining(rental.seconds_remaining);
  //   }
  // }, [rental.seconds_remaining, rental.id, isNoLimit]);

  // Timer logic: COUNT DOWN for paket/durasi, COUNT UP for no_limit
  // Using server-based calculation to avoid browser throttling when tab is inactive
  useEffect(() => {
    if (!rental.id) return; // Safety check
    
    // If rental is paused, don't update timer
    if (rental.is_paused) {
      console.log('[Timer] Rental is PAUSED, timer frozen');
      return;
    }
    
    const updateTimer = () => {
      try {
        if (isNoLimit) {
          // No Limit: Calculate elapsed time from server start_time
          const startTime = new Date(rental.start_time).getTime();
          const now = Date.now();
          const elapsedSeconds = Math.floor((now - startTime) / 1000);
          
          // Subtract total pause duration with validation
          let totalPauseMinutes = rental.total_pause_minutes || 0;
          
          // Validate pause minutes (should be reasonable, not negative or extremely large)
          if (isNaN(totalPauseMinutes) || totalPauseMinutes < 0 || totalPauseMinutes > 10000) {
            console.error('[Timer] Invalid total_pause_minutes:', totalPauseMinutes, 'Resetting to 0');
            totalPauseMinutes = 0;
          }
          
          const totalPauseSeconds = Math.floor(totalPauseMinutes * 60);
          const billableSeconds = Math.max(0, elapsedSeconds - totalPauseSeconds);
          
          setTimeRemaining(billableSeconds);
        } else {
          // Paket/Durasi: Calculate from rental.end_time (always from local state)
          if (rental.end_time) {
            const endTime = new Date(rental.end_time).getTime();
            const now = Date.now();
            const remainingSeconds = Math.max(0, Math.floor((endTime - now) / 1000));
            setTimeRemaining(remainingSeconds);
          }
        }
      } catch (error) {
        console.error('[Timer] Error updating time:', error);
      }
    };
    
    // Update immediately
    updateTimer();
    
    // Then update every second
    const timer = setInterval(updateTimer, 1000);

    return () => clearInterval(timer);
  }, [isNoLimit, rental.id, rental.end_time, rental.start_time, rental.is_paused, rental.total_pause_minutes]);

  const formatTime = (seconds) => {
    // Validate input
    if (!seconds || isNaN(seconds) || seconds < 0) {
      return '0:00';
    }
    
    // Ensure it's an integer
    const totalSeconds = Math.floor(seconds);
    
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const isExpiringSoon = !isNoLimit && timeRemaining <= 600; // 10 minutes
  const isExpired = !isNoLimit && timeRemaining <= 0;
  
  // Calculate F&B subtotal
  const fbSubtotal = (rental.additional_items || []).reduce((sum, item) => {
    return sum + (item.price * item.qty);
  }, 0);
  
  // Calculate BASE rental charge (without F&B)
  // For no_limit: calculate based on elapsed time
  // For paket/duration: total_price already includes F&B, so subtract it to get base
  const rentalCharge = isNoLimit 
    ? Math.max(1, Math.ceil(timeRemaining / 3600)) * (rental.price_per_hour || 0)
    : (rental.total_price || 0) - fbSubtotal;  // Subtract F&B to get base rental price
  
  const currentCharge = rentalCharge + fbSubtotal;
  
  // Fallback values for missing fields - MUST BE OUTSIDE try-catch for render access
  const psUnitName = rental.ps_unit_name || rental.unit_name || 'Unit Unknown';
  const customerName = rental.customer_name || 'Walk-in Customer';
  
  // Safety checks for render
  if (!rental.start_time || !rental.rental_mode) {
    console.error('[ActiveRentalCard] Critically missing required fields:', rental);
    return (
      <Card className="bg-red-900/20 border-red-500">
        <CardContent className="p-4">
          <p className="text-red-300 text-sm">Error: Data rental tidak lengkap</p>
          <p className="text-red-400 text-xs mt-1">ID: {rental.id}</p>
        </CardContent>
      </Card>
    );
  }

  const handleStopClick = (earlyStop = false) => {
    if (!earlyStop) {
      setShowStopConfirm(true);
    } else {
      handleStopConfirmed(earlyStop);
    }
  };

  const handleStopConfirmed = async (earlyStop = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await axios.post(
        `${API}/rentals/${rental.id}/stop?early_stop=${earlyStop}`,
        {},
        { headers }
      );

      // If we got here without error, stop was successful
      const finalPrice = response.data?.final_price || response.data?.total_price || 0;
      toast.success(` Rental dihentikan. Total: Rp ${finalPrice.toLocaleString('id-ID')}`, {
        duration: 3000,
        style: {
          background: 'linear-gradient(to right, #10b981, #059669)',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '16px',
          border: '2px solid #34d399',
          boxShadow: '0 4px 20px rgba(16, 185, 129, 0.4)'
        }
      });
      
      // Call parent callback to refresh
      if (onStopped) {
        onStopped();
      }
    } catch (error) {
      console.error("Error stopping rental:", error);
      toast.error(" Gagal menghentikan rental: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleExtend = async () => {
    // Prevent double click
    if (loading) return;
    
    try {
      setLoading(true);
      
      // Convert to hours
      const additionalHours = extendUnit === 'hours' 
        ? extendDuration 
        : extendDuration / 60;
      
      const response = await axios.post(`${API}/rentals/${rental.id}/extend`, {
        additional_hours: additionalHours
      });

      if (response.data.success) {
        const durationText = extendUnit === 'hours' 
          ? `${extendDuration} jam` 
          : `${extendDuration} menit`;
        toast.success(`Rental diperpanjang ${durationText}`);
        setShowExtendDialog(false);
        
        // IMMEDIATELY fetch fresh rental data from API (not parent!)
        console.log('[Extend] Fetching fresh rental data from API...');
        const freshRentalResponse = await axios.get(`${API}/rentals/${rental.id}`);
        
        if (freshRentalResponse.data) {
          console.log('[Extend] Got fresh data, updating local state:', freshRentalResponse.data);
          setRental(freshRentalResponse.data); // Update local state
          
          // Timer will auto-update via useEffect when rental.end_time changes
          console.log('[Extend] Local rental state updated, timer will recalculate');
        }
        
        // Then trigger parent refresh for consistency
        if (onExtended) {
          onExtended();
        }
      } else {
        // Backend returned success: false
        toast.error(response.data.error || "Gagal memperpanjang rental");
      }
    } catch (error) {
      console.error("Error extending rental:", error);
      // Only show error if it's a network/server error
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || "Gagal memperpanjang rental";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };


  const handleTransferred = (updatedRental) => {
    // Update local state immediately with new rental data from backend
    setRental(updatedRental);
    
    // Also trigger parent refresh to ensure data consistency
    if (onExtended) {
      onExtended();
    }
  };


  const handleAddOrder = async (rentalId, items, isRefreshOnly = false) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // If refresh only (from item deletion), skip the API call to add items
      if (isRefreshOnly) {
        console.log('[AddOrder] Refresh only - fetching fresh rental data...');
        const freshRentalResponse = await axios.get(`${API}/rentals/${rental.id}`, { headers });
        
        if (freshRentalResponse.data) {
          console.log('[AddOrder] Got fresh data, updating local state:', freshRentalResponse.data);
          setRental(freshRentalResponse.data); // Update local state
        }
        
        // Trigger parent refresh
        if (onExtended && typeof onExtended === 'function') {
          onExtended();
        }
        return;
      }
      
      console.log('Adding order to rental:', rentalId, 'Items:', items);
      
      const response = await axios.post(
        `${API}/rentals/${rentalId}/add-order`,
        { items },
        { headers }
      );

      console.log('Add order response:', response.data);

      if (response.data.success) {
        // Success - let AddOrderDialog show its own toast
        setShowAddOrderDialog(false);
        
        // IMMEDIATELY fetch fresh rental data from API (similar to extend logic)
        console.log('[AddOrder] Fetching fresh rental data from API...');
        const freshRentalResponse = await axios.get(`${API}/rentals/${rental.id}`, { headers });
        
        if (freshRentalResponse.data) {
          console.log('[AddOrder] Got fresh data, updating local state:', freshRentalResponse.data);
          setRental(freshRentalResponse.data); // Update local state
          
          // The UI will auto-update when rental state changes
          console.log('[AddOrder] Local rental state updated, UI will refresh');
        }
        
        // Then trigger parent refresh for consistency with other cards
        if (onExtended && typeof onExtended === 'function') {
          onExtended();
        }
      } else {
        // Backend returned success: false
        throw new Error(response.data.error || 'Failed to add order');
      }
    } catch (error) {
      console.error(" Error adding order:", error);
      console.error("   Error details:", error.response?.data);
      throw error;
    }
  };

  return (
    <>
      <Card className={`billing-active-rental-card bg-gradient-to-br ${
        isExpired ? 'from-red-500/70 to-red-600/70 border-red-400/50' : 
        isExpiringSoon && !isNoLimit ? 'from-yellow-500/70 to-yellow-600/70 border-yellow-400/50' :
        rentalMode === 'no_limit' ? 'from-purple-500/70 to-purple-600/70 border-purple-400/50' :
        rentalMode === 'paket' ? 'from-green-500/70 to-green-600/70 border-green-400/50' :
        'from-blue-500/70 to-blue-600/70 border-blue-400/50'
      } border-2 rounded-xl shadow-xl hover:scale-[1.02] transition-all duration-500 ease-out`}>
        <CardContent className="p-5">
          <div className="text-center space-y-3">
            {/* Icon Gamepad */}
            <div className="flex justify-center mb-2">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full animate-pulse">
                <Gamepad2 className="h-8 w-8 text-white" />
              </div>
            </div>
            
            {/* Rental Mode Badge with Duration Info */}
            <div className="flex justify-center">
              <span className="px-4 py-1 bg-white/30 rounded-full shadow-lg text-sm font-bold text-white">
                {rentalMode === 'paket' ? ' PAKET' :
                 rentalMode === 'no_limit' ? '∞ NO LIMIT' :
                 rental.duration_hours ? ` DURASI - ${rental.duration_hours} Jam` : ' DURASI'}
              </span>
            </div>
            
            {/* Branch Name Badge */}
            {rental.branch && (
              <div className="flex justify-center">
                <span className="px-3 py-0.5 bg-white/20 rounded-full shadow text-xs font-semibold text-white/90">
                   {rental.branch}
                </span>
              </div>
            )}
            
            {/* PS Unit Name - BESAR */}
            <h3 className="text-3xl font-bold text-white drop-shadow-lg">
              {psUnitName}
            </h3>
            
            {/* Status Badge */}
            <div className="flex items-center justify-center gap-1">
              {rental.is_paused ? (
                <>
                  <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                  <span className="text-xs text-yellow-300 font-bold uppercase animate-pulse">
                     PAUSED - Mati Listrik
                  </span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 bg-white rounded-full animate-ping"></span>
                  <span className="text-xs text-white font-semibold uppercase">
                    {isExpired ? ' EXPIRED' : isExpiringSoon && !isNoLimit ? ' EXPIRING' : ' BERJALAN'}
                  </span>
                </>
              )}
            </div>
            
            {/* Timer - BESAR */}
            <div className="bg-slate-900/50 rounded-xl p-4 border-2 border-white/20">
              <p className="text-xs text-white/80 uppercase tracking-wider mb-1">
                {isNoLimit ? ' Waktu Berjalan' : ' Waktu Tersisa'}
              </p>
              <div className="text-5xl font-black text-white drop-shadow-lg" style={{ fontFamily: 'monospace' }}>
                {formatTime(timeRemaining)}
              </div>
              {isNoLimit && (
                <p className="text-xs text-white/70 mt-1">Charge per jam</p>
              )}
            </div>
            
            {/* Customer Info */}
            <div className="bg-white/10 rounded-lg p-3 space-y-2 text-left">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-white/80" />
                <span className="text-white font-semibold text-sm">{customerName}</span>
              </div>
              {rental.customer_phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-white/80" />
                  <span className="text-white/90 text-sm">{rental.customer_phone}</span>
                </div>
              )}
            </div>
            
            {/* Pricing - Gaming Style */}
            <div className="bg-white/10 rounded-lg p-3 space-y-2">
              {rental.package_name && (
                <div className="text-center pb-2 border-b border-white/20">
                  <span className="text-xs text-white/70">Paket</span>
                  <p className="text-white font-bold">{rental.package_name}</p>
                </div>
              )}
              
              <div className="flex justify-between items-center">
                <span className="text-white/80 text-sm">
                  {isNoLimit ? ' Rental' : ' Durasi'}
                </span>
                <span className="text-white font-bold">
                  Rp {rentalCharge.toLocaleString('id-ID')}
                </span>
              </div>
              
              {isNoLimit && (
                <p className="text-xs text-white/60 text-right">
                  {Math.ceil(timeRemaining / 3600)} jam × Rp {(rental.price_per_hour || 0).toLocaleString('id-ID')}
                </p>
              )}
              
              {/* F&B if any */}
              {fbSubtotal > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-orange-300 text-sm">
                     F&B ({rental.additional_items.length})
                  </span>
                  <span className="text-white font-bold">
                    Rp {fbSubtotal.toLocaleString('id-ID')}
                  </span>
                </div>
              )}
              
              {/* Total */}
              <div className="flex justify-between items-center pt-2 mt-2 border-t-2 border-white/30">
                <span className="font-bold text-white text-lg">
                   TOTAL
                </span>
                <span className="font-black text-white text-2xl">
                  Rp {currentCharge.toLocaleString('id-ID')}
                </span>
              </div>
            </div>

          {/* Actions - Compact Buttons */}
          <div className={`grid gap-2 ${isNoLimit ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {!isNoLimit && (
              <button
                onClick={() => setShowExtendDialog(true)}
                className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white py-2 px-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-1.5 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                <Clock className="h-3.5 w-3.5" />
                <span>Extend</span>
              </button>
            )}
            <button
              onClick={() => setShowAddOrderDialog(true)}
              className="bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white py-2 px-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-1.5 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              <ShoppingCart className="h-3.5 w-3.5" />
              <span>F&B</span>
            </button>
            <button
              onClick={() => setShowTransferDialog(true)}
              className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white py-2 px-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-1.5 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              <ArrowRightLeft className="h-3.5 w-3.5" />
              <span>Pindah</span>
            </button>
            <button
              onClick={() => handleStopClick(false)}
              className="bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white py-2 px-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-1.5 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Square className="h-3.5 w-3.5" />
              )}
              <span>Stop</span>
            </button>
          </div>
          </div>
        </CardContent>
      </Card>

      {/* Extend Dialog */}
      <Dialog open={showExtendDialog} onOpenChange={setShowExtendDialog}>
        <DialogContent className="bg-slate-900 border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>Perpanjang Rental</DialogTitle>
            <DialogDescription className="text-blue-200">
              {customerName} - {psUnitName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Unit Selection */}
            <div>
              <Label>Satuan Waktu</Label>
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => {
                    setExtendUnit('minutes');
                    setExtendDuration(30);
                  }}
                  className={`flex-1 px-4 py-2 rounded-lg transition ${
                    extendUnit === 'minutes'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  Menit
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setExtendUnit('hours');
                    setExtendDuration(1);
                  }}
                  className={`flex-1 px-4 py-2 rounded-lg transition ${
                    extendUnit === 'hours'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  Jam
                </button>
              </div>
            </div>

            {/* Preset Options */}
            <div>
              <Label>Pilih Durasi</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {extendUnit === 'minutes' ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setExtendDuration(15)}
                      className={`px-3 py-2 rounded-lg text-sm transition ${
                        extendDuration === 15
                          ? 'bg-blue-600 text-white'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      15m
                    </button>
                    <button
                      type="button"
                      onClick={() => setExtendDuration(30)}
                      className={`px-3 py-2 rounded-lg text-sm transition ${
                        extendDuration === 30
                          ? 'bg-blue-600 text-white'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      30m
                    </button>
                    <button
                      type="button"
                      onClick={() => setExtendDuration(45)}
                      className={`px-3 py-2 rounded-lg text-sm transition ${
                        extendDuration === 45
                          ? 'bg-blue-600 text-white'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      45m
                    </button>
                    <button
                      type="button"
                      onClick={() => setExtendDuration(60)}
                      className={`px-3 py-2 rounded-lg text-sm transition ${
                        extendDuration === 60
                          ? 'bg-blue-600 text-white'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      60m
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setExtendDuration(1)}
                      className={`px-3 py-2 rounded-lg text-sm transition ${
                        extendDuration === 1
                          ? 'bg-blue-600 text-white'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      1h
                    </button>
                    <button
                      type="button"
                      onClick={() => setExtendDuration(2)}
                      className={`px-3 py-2 rounded-lg text-sm transition ${
                        extendDuration === 2
                          ? 'bg-blue-600 text-white'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      2h
                    </button>
                    <button
                      type="button"
                      onClick={() => setExtendDuration(3)}
                      className={`px-3 py-2 rounded-lg text-sm transition ${
                        extendDuration === 3
                          ? 'bg-blue-600 text-white'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      3h
                    </button>
                    <button
                      type="button"
                      onClick={() => setExtendDuration(4)}
                      className={`px-3 py-2 rounded-lg text-sm transition ${
                        extendDuration === 4
                          ? 'bg-blue-600 text-white'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      4h
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Custom Input */}
            <div>
              <Label htmlFor="extend_custom">Atau Custom ({extendUnit === 'hours' ? 'Jam' : 'Menit'})</Label>
              <Input
                id="extend_custom"
                type="number"
                value={extendDuration}
                onChange={(e) => setExtendDuration(parseFloat(e.target.value) || 0)}
                className="bg-white/10 border-white/20 text-white"
                min={extendUnit === 'hours' ? 0.5 : 5}
                step={extendUnit === 'hours' ? 0.5 : 5}
              />
            </div>

            {/* Price Calculation */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex justify-between">
                <span className="text-blue-200">Tambahan Biaya:</span>
                <span className="text-white font-bold">
                  Rp {((extendUnit === 'hours' ? extendDuration : extendDuration / 60) * rental.price_per_hour).toLocaleString('id-ID')}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <button
              onClick={() => setShowExtendDialog(false)}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Batal
            </button>
            <button
              onClick={handleExtend}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin inline" />
                  Memproses...
                </>
              ) : (
                "Perpanjang"
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Order Dialog */}
      <AddOrderDialog
        rental={rental}
        open={showAddOrderDialog}
        onClose={() => setShowAddOrderDialog(false)}
        onOrderSuccess={handleAddOrder}
      />

      {/* Transfer Unit Dialog */}
      <TransferUnitDialog
        open={showTransferDialog}
        onClose={() => setShowTransferDialog(false)}
        rental={rental}
        onTransferred={handleTransferred}
      />

      {/* Stop Confirmation Dialog */}
      <ConfirmDialog
        open={showStopConfirm}
        onClose={() => setShowStopConfirm(false)}
        onConfirm={() => handleStopConfirmed(false)}
        title="Konfirmasi Hentikan Rental"
        description={`Yakin ingin menghentikan rental untuk ${customerName}?`}
        confirmText="Ya, Hentikan"
        cancelText="Batal"
        variant="danger"
      />
    </>
  );
}
